/** @jsxRuntime classic */
/** @jsx jsx */
import {FC, Fragment, useEffect, useState} from 'react';
import {css, jsx} from '@emotion/react';
import {stringify as queryStringify} from 'query-string';
import tokens from '@flexe/ui-tokens';
import {InfoBox} from '@flexe/ui-components';
import {renderItemLink} from '../../../../libs/helpers';
import {InboundState, InventoryProperties, ItemQuantity, SkuContent} from '../InboundInterfaces';
import {convertToCartons, convertToEaches, pluralizeUnit} from '../../../shared/utilities/DataFormatting';
import {Packaging} from '../../../shared/CommonInterfaces';
import PackingListService, {UpdatePackingListRequest} from '../../../shared/services/PackingListService';
import ContainersService from '../../../shared/services/ContainersService';
import EditLpnMenu from '../../../shared/EditLpnMenu';
import UpdateControls from '../../../dropoffs/shared/UpdateControls';

interface InboundSkuContentRowProps {
  content: SkuContent;
  inboundId: number;
  isShipper: boolean;
  inboundState: InboundState;
  showUoms: boolean;
  shipperProps?: {
    containersService: ContainersService;
    packingListService: PackingListService;
    handleRefreshPage: () => void;
  };
  showQuantityCallback: () => void;
}

const PrintSkuLabelLink: FC<{
  inboundId: number;
  inventoryId: number;
  quantity: number;
  unit: string;
}> = ({inboundId, inventoryId, quantity, unit}) => {
  const params = queryStringify({
    inventoryId,
    numShippable: quantity,
    numDamaged: 0,
    unit
  });
  const printSkuLabelUrl = `/api/v2/containers/${inboundId}/content_labels.pdf?${params}`;
  return (
    <a className="btn" target="_blank" href={printSkuLabelUrl} id="btn-print-sku">
      <i className="fa fa-print" /> Print
    </a>
  );
};

const getReceivedQuantityDisplay = (
  received: ItemQuantity[],
  properties: InventoryProperties,
  isDamaged: boolean,
  expectedUom?: Packaging
): string => {
  const receivedUnits = new Set(received.filter((r) => r.isDamaged === isDamaged).map((r) => r.packaging));
  const receivedInMultipleUnits = receivedUnits.size > 1;
  const singleReceivedUnit = receivedUnits.size === 1 ? received[0].packaging : expectedUom || Packaging.each;

  if (!receivedInMultipleUnits && (expectedUom == null || singleReceivedUnit === expectedUom)) {
    const totalQuantity = calculateTotalQuantity(received, properties, isDamaged, singleReceivedUnit);
    return `${totalQuantity} ${pluralizeUnit(singleReceivedUnit, totalQuantity)}`;
  }

  // Otherwise, show conversions.
  const totalEaches = calculateTotalQuantity(received, properties, isDamaged, Packaging.each);
  const totalCartons = calculateTotalQuantity(received, properties, isDamaged, Packaging.carton);

  if (expectedUom?.toLowerCase() === 'carton') {
    return `${Math.trunc(totalCartons)} ${pluralizeUnit(Packaging.carton, totalCartons)} (${Math.round(
      totalEaches
    )} ${pluralizeUnit(Packaging.each, totalEaches)})`;
  } else {
    return `${Math.round(totalEaches)} ${pluralizeUnit(Packaging.each, totalEaches)} (${Math.trunc(
      totalCartons
    )} ${pluralizeUnit(Packaging.carton, totalCartons)})`;
  }
};

const calculateTotalQuantity = (
  quantities: ItemQuantity[],
  properties: InventoryProperties,
  isDamaged: boolean,
  targetUnit: Packaging
): number => {
  return quantities
    .filter((q) => q.isDamaged === isDamaged)
    .reduce((total, current) => {
      const convertedValue =
        targetUnit === Packaging.carton
          ? convertToCartons(current.amount, current.packaging, properties?.each?.unitsPerParent)
          : convertToEaches(current.amount, current.packaging, properties?.each?.unitsPerParent);
      return total + convertedValue;
    }, 0);
};

export const SkuContentRow: FC<InboundSkuContentRowProps> = (props) => {
  const {
    content,
    inboundId,
    inboundState,
    showUoms,
    isShipper,
    shipperProps: {containersService, handleRefreshPage, packingListService} = {},
    showQuantityCallback
  } = props;
  const BLANK_COLUMN = '-';
  const [editState, setEditState] = useState<'view' | 'edit' | 'removalConfirmation'>('view');
  const [showDropdown, setShowDropdown] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [updatedProperties, setUpdatedProperties] = useState({expectedQuantity: content.expectedQuantity});

  const totalReceivedQuantitiesWithoutDamagedDisplay = getReceivedQuantityDisplay(
    content.receivedQuantities,
    content.item.properties,
    false,
    content.expectedQuantity?.packaging
  );

  const damagedDisplay = getReceivedQuantityDisplay(
    content.receivedQuantities,
    content.item.properties,
    true,
    content.expectedQuantity?.packaging
  );

  const areQuantitiesMismatched =
    content.expectedQuantity === null ||
    content.expectedQuantity.amount === 0 ||
    convertToEaches(
      content.expectedQuantity.amount,
      content.expectedQuantity.packaging,
      content.item.properties?.each?.unitsPerParent
    ) !== calculateTotalQuantity(content.receivedQuantities, content.item.properties, false, Packaging.each);

  useEffect(() => {
    if (areQuantitiesMismatched) {
      showQuantityCallback();
    }
  }, [areQuantitiesMismatched, showQuantityCallback]);

  useEffect(() => {
    const closeDropdownHandler: EventListener = (event) => {
      let element = event.target as Element;
      while (element.parentElement) {
        if (element.classList && element.classList.contains('dropdown-menu')) {
          // Do nothing if clicked within the dropdown
          return;
        } else {
          element = element.parentElement;
        }
      }
      setShowDropdown(false);
    };
    document.body.addEventListener('mouseup', closeDropdownHandler);

    return () => document.body.removeEventListener('mouseup', closeDropdownHandler);
  }, []);

  const updateSku = async () => {
    const packingListIdToUpdate = content.expectedQuantity?.packingListId || -1;

    if (!isShipper || packingListIdToUpdate === -1) return;

    const updateItemRequest: UpdatePackingListRequest = {
      packingListId: packingListIdToUpdate,
      quantity: updatedProperties.expectedQuantity.amount
    };
    const response = await packingListService.updatePackingList(updateItemRequest);
    if (response.errors && response.errors.length > 0) {
      setErrors(response.errors.map((error) => error.detail));
    } else {
      handleRefreshPage();
    }
    setEditState('view');
  };

  const toggleConfirmationModal = () =>
    setEditState(editState === 'removalConfirmation' ? 'view' : 'removalConfirmation');

  const removeSku = async () => {
    const packingListIdToUpdate = content.expectedQuantity?.packingListId || -1;

    if (!isShipper || packingListIdToUpdate === -1) return;

    const response = await containersService.deletePackingList(inboundId, packingListIdToUpdate);
    if (response.status === 'success') {
      handleRefreshPage();
    } else {
      setErrors([response.message]);
    }
    setEditState('view');
  };

  const renderUomData = (innerPack: number, outerCase: number) => (
    <div>
      <span>{innerPack || BLANK_COLUMN} each/inner pack</span>
      <br />
      <span>{outerCase || BLANK_COLUMN} each/outer case</span>
    </div>
  );

  return (
    <div data-testid="inbound-sku-content-row" className="lpn-row">
      {editState === 'removalConfirmation' && (
        <div className="delete-modal">
          <div className="modal-backdrop"></div>
          <div className="modal" tabIndex={-1} role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <button type="button" className="close" aria-label="Close" onClick={toggleConfirmationModal}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                  <h5 className="modal-title">
                    Delete SKU {content.item.sku} with Purchase Order {content.purchaseOrder}
                  </h5>
                </div>
                <div className="modal-body">Are you sure you want to remove this item?</div>
                <div className="modal-footer">
                  <button type="button" onClick={removeSku} className="btn">
                    Yes, remove this item
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {errors.map((error) => (
        <InfoBox info={error} infoType="error" />
      ))}

      <div
        className="lpn-row row"
        css={css`
          background-color: ${areQuantitiesMismatched &&
          [InboundState.Receiving, InboundState.Completed].includes(props.inboundState)
            ? tokens.color.base.yellow.v50.value
            : 'inherit'};
        `}
      >
        <div className="col-md-2">{renderItemLink(content.item.inventoryId, content.item.sku, isShipper)}</div>
        <div className="col-md-2">{content.item.description || BLANK_COLUMN}</div>
        <div className="col-md-2">{content.purchaseOrder || BLANK_COLUMN}</div>
        {showUoms && (
          <Fragment>
            <div className="col-md-1">
              {renderUomData(
                content.expectedQuantity?.uoms?.eachesPerInnerPack,
                content.expectedQuantity?.uoms?.eachesPerOuterCase
              )}
            </div>
            <div className="col-md-1">
              {renderUomData(
                content.receivedQuantities[0]?.uoms?.eachesPerInnerPack,
                content.receivedQuantities[0]?.uoms?.eachesPerOuterCase
              )}
            </div>
          </Fragment>
        )}
        {editState === 'edit' && content.expectedQuantity ? (
          <div className="col-md-1">
            <div className="inner-outer-container">
              <input
                type="number"
                className="form-control min-input-width"
                min={1}
                value={updatedProperties.expectedQuantity.amount}
                onChange={(newQuantity) => {
                  setUpdatedProperties({
                    expectedQuantity: {
                      ...content.expectedQuantity,
                      amount: Number(newQuantity.target.value)
                    }
                  });
                }}
              />
              <div className="inner-outer-text">{content.expectedQuantity?.amount}</div>
            </div>
          </div>
        ) : (
          <div className="col-md-1">
            {content.expectedQuantity?.amount || 0}{' '}
            {pluralizeUnit(content.expectedQuantity?.packaging, content.expectedQuantity?.amount) ||
              pluralizeUnit(Packaging.each, 0)}
          </div>
        )}
        <div className="col-md-2">{totalReceivedQuantitiesWithoutDamagedDisplay}</div>
        <div className="col-md-1">{damagedDisplay}</div>
        {!isShipper && (
          <div className="col-md-1">
            <PrintSkuLabelLink
              inboundId={inboundId}
              inventoryId={content.item.inventoryId}
              quantity={calculateTotalQuantity(
                content.receivedQuantities,
                content.item.properties,
                false,
                content.expectedQuantity?.packaging || Packaging.each
              )}
              unit={content.expectedQuantity?.packaging || Packaging.each}
            />
          </div>
        )}
        {isShipper &&
          editState !== 'edit' &&
          [InboundState.New, InboundState.Confirmed, InboundState.Altered].includes(inboundState) && (
            <div className="col-md-1">
              <EditLpnMenu
                showDropdown={showDropdown}
                showMoveToList={false}
                containerDeliveryId={null}
                inProgressDropoffIds={[]}
                showEditToggle={true}
                toggleShowDropdown={() => {
                  setShowDropdown(!showDropdown);
                }}
                toggleShowMoveToList={null}
                editLpn={null}
                moveLPNto={null}
                confirmLPNDelete={null}
                editSkuMenuProps={{
                  editSku: () => setEditState('edit'),
                  confirmRemoveSku: () => setEditState('removalConfirmation')
                }}
              />
            </div>
          )}
        {editState === 'edit' && (
          <UpdateControls saveButtonEnabled={true} cancelHandler={() => setEditState('view')} saveHandler={updateSku} />
        )}
      </div>
    </div>
  );
};
